#!/usr/bin/env node
/**
 * croniq CLI — understand a cron expression in your terminal. Zero-dependency.
 *
 *   croniq "0 9 * * 1"                  # describe + next runs
 *   croniq next "every 15 min" -n 5     # the next 5 run times
 *   croniq describe "0 0 1 * *"         # plain-English description
 *   croniq valid "61 * * * *"           # exit 1 if invalid
 */

import { Cron, isValid, parse } from "./index.js";
import pkg from "../package.json";

const HELP = `croniq — parse, describe and compute runs for cron expressions.

Usage:
  croniq <expression>            Describe it and show the next few runs
  croniq describe <expression>   Plain-English description only
  croniq next <expression>       Next run time(s) (use -n for more)
  croniq prev <expression>       Previous run time
  croniq valid <expression>      Exit 0 if valid, 1 if not

Options:
  -n, --count <n>   How many runs to list (default 5 for \`next\`)
      --utc         Interpret/print times in UTC
      --iso         Print times as ISO 8601
  -h, --help        Show this help
  -v, --version     Show version

Supports standard 5-field cron (with ranges, steps, lists, names). All local.`;

interface Flags {
  positional: string[];
  count: number | null;
  utc: boolean;
  iso: boolean;
}

function parseFlags(argv: string[]): Flags {
  const f: Flags = { positional: [], count: null, utc: false, iso: false };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i]!;
    if (a === "-n" || a === "--count") f.count = Math.max(1, Number(argv[++i]) || 1);
    else if (a === "--utc") f.utc = true;
    else if (a === "--iso") f.iso = true;
    else if (!a.startsWith("-")) f.positional.push(a);
  }
  return f;
}

function fmt(d: Date, f: Flags): string {
  if (f.iso) return d.toISOString();
  return f.utc ? d.toUTCString() : d.toString();
}

function main(): number {
  const argv = process.argv.slice(2);
  if (argv.length === 0 || argv.includes("-h") || argv.includes("--help")) {
    process.stdout.write(HELP + "\n");
    return argv.length === 0 ? 2 : 0;
  }
  if (argv[0] === "-v" || argv[0] === "--version") {
    process.stdout.write(`croniq ${pkg.version}\n`);
    return 0;
  }

  const f = parseFlags(argv);
  const sub = new Set(["describe", "next", "prev", "valid"]);
  const cmd = sub.has(f.positional[0] ?? "") ? f.positional[0]! : "show";
  const expr = cmd === "show" ? f.positional.join(" ") : f.positional.slice(1).join(" ");

  if (!expr) {
    process.stderr.write("croniq: provide a cron expression. See --help.\n");
    return 2;
  }

  if (cmd === "valid") {
    const ok = isValid(expr, f.utc ? { utc: true } : undefined);
    if (!ok) process.stderr.write(`croniq: "${expr}" is not a valid cron expression\n`);
    return ok ? 0 : 1;
  }

  let cron: Cron;
  try {
    cron = parse(expr, f.utc ? { utc: true } : undefined);
  } catch (e) {
    process.stderr.write(`croniq: ${(e as Error).message}\n`);
    return 1;
  }

  switch (cmd) {
    case "describe":
      process.stdout.write(cron.describe() + "\n");
      return 0;
    case "prev":
      process.stdout.write(fmt(cron.prev(), f) + "\n");
      return 0;
    case "next": {
      const runs = cron.nextN(f.count ?? 5);
      process.stdout.write(runs.map((d) => fmt(d, f)).join("\n") + "\n");
      return 0;
    }
    default: {
      // show: description + next N runs
      process.stdout.write(`${cron.describe()}\n\nNext runs:\n`);
      const runs = cron.nextN(f.count ?? 5);
      process.stdout.write(runs.map((d) => "  " + fmt(d, f)).join("\n") + "\n");
      return 0;
    }
  }
}

process.exit(main());
